import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper,
  Typography,
  CircularProgress,
  Box,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
  Grid,
  InputAdornment,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import DateRangeIcon from '@mui/icons-material/DateRange';

// API call to fetch cancelled transfer bonds
const getCancelledRequests = async (token, retrieveAll = false) => {
  const baseURL = process.env.REACT_APP_BASE_URL;
  try {
    // Construct the URL with or without the query parameter
    const url = retrieveAll
      ? `${baseURL}requestingtransferbond/cancelled?retrieveAll=true`
      : `${baseURL}requestingtransferbond/cancelled`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new Error(`Error fetching cancelled requests: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Error fetching cancelled requests:', error);
    toast.error('Error fetching cancelled requests.');
    throw error;
  }
};

const CancelledRequestsGrid = ({ canViewPostingAgency }) => {
  const navigate = useNavigate(); // For navigation to view details
  const [groupedRows, setGroupedRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [searchRef, setSearchRef] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Fetch the cancelled requests data and group by dbaAgencyName
  const fetchCancelledRequests = useCallback(async () => {
    setLoading(true);
    const token = localStorage.getItem('jwtToken');
    if (!token) {
      toast.error('No authentication token found.');
      setLoading(false);
      return;
    }

    try {
      const data = await getCancelledRequests(token, showAll);

      // Group the data by dbaAgencyName
      const groupedData = data.reduce((acc, request) => {
        let agencyGroup = acc.find(
          (group) => group.dbaAgencyName === request.dbaAgencyName
        );
        if (!agencyGroup) {
          agencyGroup = {
            dbaAgencyName: request.dbaAgencyName,
            records: [],
          };
          acc.push(agencyGroup);
        }
        let dateCancelledFormatted = 'N/A';
        if (request.cancelledDate) {
          const dateObj = new Date(request.cancelledDate);
          const dateStr = dateObj.toLocaleDateString('en-US');
          const timeStr = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
          dateCancelledFormatted = { date: dateStr, time: timeStr };
        }

        agencyGroup.records.push({
          id: request.transferBondTransactionID,
          defendantName: `${request.defendantFirstName || ''} ${request.defendantLastName || ''}`.trim() || 'N/A',
          refNumber: request.transferBondTransactionID.toString(),
          reason: request.cancelledReason || 'N/A',
          dateCancelled: request.cancelledDate ? new Date(request.cancelledDate) : null,
          dateCancelledFormatted: dateCancelledFormatted,
          details: request.cancelledReason || 'No details provided',
          postingAgencyName: request.postingAgencyName || '-',
        });
        return acc;
      }, []);

      setGroupedRows(groupedData);
    } catch (error) {
      // Error handled in the API call
    } finally {
      setLoading(false); // Stop loading when data is fetched or error occurs
    }
  }, [showAll]);

  // Fetch cancelled requests when component mounts or when showAll changes
  useEffect(() => {
    fetchCancelledRequests();
  }, [fetchCancelledRequests]);

  const handleViewDetails = (id) => {
    navigate(`/view-details?ID=${id}`);
  };

  const columns = [
    {
      field: 'refNumber',
      headerName: 'Ref #',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          height: '100%',
          width: '100%'
        }}>
          <Typography variant="body2">{params.value}</Typography>
          <IconButton
            color="primary"
            size="small"
            onClick={() => handleViewDetails(params.row.id)}
          >
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
    },
    {
      field: 'defendantName',
      headerName: 'Defendant Name',
      width: 200,
      align: 'center',
      headerAlign: 'center',
    },
    ...(canViewPostingAgency
      ? [
          {
            field: 'postingAgencyName',
            headerName: 'Posting Agency',
            width: 200,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
              <Typography
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  whiteSpace: 'normal',
                  wordWrap: 'break-word',
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                  height: '100%',
                }}
              >
                {params.row.postingAgencyName || '-'}
              </Typography>
            ),
          },
        ]
      : []),
    { field: 'reason', headerName: 'Reason', width: 200 },
    {
      field: 'dateCancelledFormatted',
      headerName: 'Date Cancelled',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        if (params.value === 'N/A' || typeof params.value === 'string') {
          return <Typography variant="body2">N/A</Typography>;
        }
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2">{params.value.date}</Typography>
            <Typography variant="body2" sx={{ fontSize: '0.85rem', color: 'text.secondary' }}>
              {params.value.time}
            </Typography>
          </Box>
        );
      },
    },
    { field: 'details', headerName: 'Details', width: 250 },
  ];

  // Filter the data based on search input and date range
  const getFilteredRows = () => {
    let filteredData = groupedRows.map((group) => {
      let records = group.records;

      // Filter by reference number
      if (searchRef.trim() !== '') {
        records = records.filter((record) =>
          record.refNumber.includes(searchRef.trim())
        );
      }

      // Filter by date range (only when showing all)
      if (showAll && startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        // Include end date's entire day
        end.setHours(23, 59, 59, 999);

        records = records.filter(
          (record) =>
            record.dateCancelled &&
            record.dateCancelled >= start &&
            record.dateCancelled <= end
        );
      }

      return {
        ...group,
        records,
      };
    });

    // Remove groups with no records after filtering
    filteredData = filteredData.filter((group) => group.records.length > 0);

    return filteredData;
  };

  return (
    <Paper sx={{ padding: 2 }}>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Cancelled Requests
      </Typography>

      {/* Toggle to Show All Cancelled Requests */}
      <FormControlLabel
        control={
          <Switch
            checked={showAll}
            onChange={(e) => {
              setShowAll(e.target.checked);
              // Reset search inputs when toggling
              setSearchRef('');
              setStartDate('');
              setEndDate('');
            }}
            color="primary"
          />
        }
        label={
          showAll ? 'Viewing All Cancelled Requests' : 'Viewing Recent Cancelled Requests'
        }
      />

      {/* Search and Date Filters */}
      <Box sx={{ mt: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          {/* Reference Number Search */}
          <Grid item xs={12} sm={4}>
            <TextField
              label="Search by Reference #"
              value={searchRef}
              onChange={(e) => setSearchRef(e.target.value)}
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          {/* Date Range Filters (Only when showing all) */}
          {showAll && (
            <>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Start Date"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DateRangeIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="End Date"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <DateRangeIcon />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </>
          )}
        </Grid>
      </Box>

      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <CircularProgress />
        </Box>
      ) : groupedRows.length > 0 ? (
        getFilteredRows().length > 0 ? (
          getFilteredRows().map((agencyGroup, index) => (
            <Box key={index} sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ mb: 2, fontSize: '1.1rem', mt: 2 }}>
                {agencyGroup.dbaAgencyName} {/* Group by dbaAgencyName */}
              </Typography>
              <Paper sx={{ width: '100%' }}>
                <DataGrid
                  rows={agencyGroup.records}
                  columns={columns}
                  initialState={{
                    pagination: {
                      paginationModel: { pageSize: 10 }, // Set initial page size to 10
                    },
                  }}
                  pageSizeOptions={[10]} // Limit rows per page options to only 10
                  pagination // Enable pagination
                  autoHeight
                  sx={{ fontSize: '0.85rem' }}
                />
              </Paper>
            </Box>
          ))
        ) : (
          // No records after filtering
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '200px',
            }}
          >
            <Typography variant="body1" color="textSecondary">
              No requests found for the given criteria.
            </Typography>
          </Box>
        )
      ) : (
        // No data from API
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '200px',
          }}
        >
          <Typography variant="body1" color="textSecondary">
            No cancelled requests found.
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default CancelledRequestsGrid;
